// PostgreSQL JSON / JSONB operator lattice.
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice C2 (pgsql row).
//
// PG distinguishes `jsonb` (binary, full operator surface) from `json`
// (text-based, narrower operator surface) — reflected in two lattices. Loose-
// JSON declarations on `text`/`varchar` columns also surface in the resolver
// via `loose-json-declared` / `loose-json-inferred`; for those we default to
// the `jsonb` lattice because user intent is "treat this column as if it were
// jsonb after a cast" — VQE-Edit (Slice D3) emits `(col::jsonb)` casts.

import type { JsonClassification } from '../../../types';
import type { Operator } from '../types';

const PG_JSONB: readonly Operator[] = Object.freeze([
  { token: '=', label: 'equals', snippetTemplate: "= '${1:value}'::jsonb" },
  { token: '<>', label: 'not equals', snippetTemplate: "<> '${1:value}'::jsonb" },
  { token: 'IS NULL', label: 'is null', snippetTemplate: 'IS NULL' },
  { token: 'IS NOT NULL', label: 'is not null', snippetTemplate: 'IS NOT NULL' },
  {
    token: '@>',
    label: 'contains',
    snippetTemplate: "@> '${1:{\"key\":\"value\"}}'::jsonb",
    docsUrl: 'https://www.postgresql.org/docs/current/functions-json.html',
  },
  {
    token: '<@',
    label: 'is contained by',
    snippetTemplate: "<@ '${1:{\"key\":\"value\"}}'::jsonb",
    docsUrl: 'https://www.postgresql.org/docs/current/functions-json.html',
  },
  {
    token: '?',
    label: 'key exists',
    snippetTemplate: "? '${1:key}'",
    docsUrl: 'https://www.postgresql.org/docs/current/functions-json.html',
  },
  {
    token: '?|',
    label: 'any key exists',
    snippetTemplate: "?| array[${1:'key1', 'key2'}]",
    docsUrl: 'https://www.postgresql.org/docs/current/functions-json.html',
  },
  {
    token: '?&',
    label: 'all keys exist',
    snippetTemplate: "?& array[${1:'key1', 'key2'}]",
    docsUrl: 'https://www.postgresql.org/docs/current/functions-json.html',
  },
  {
    token: '#>',
    label: 'extract path as jsonb',
    snippetTemplate: "#> '{${1:path,segments}}'",
    docsUrl: 'https://www.postgresql.org/docs/current/functions-json.html',
    requiresPath: true,
  },
  {
    token: '#>>',
    label: 'extract path as text',
    snippetTemplate: "#>> '{${1:path,segments}}'",
    docsUrl: 'https://www.postgresql.org/docs/current/functions-json.html',
    requiresPath: true,
  },
  {
    token: '->',
    label: 'get field as jsonb',
    snippetTemplate: "-> '${1:key}'",
    docsUrl: 'https://www.postgresql.org/docs/current/functions-json.html',
    requiresPath: true,
  },
  {
    token: '->>',
    label: 'get field as text',
    snippetTemplate: "->> '${1:key}'",
    docsUrl: 'https://www.postgresql.org/docs/current/functions-json.html',
    requiresPath: true,
  },
  {
    token: 'jsonb_path_exists',
    label: 'JSONPath exists',
    snippetTemplate: "jsonb_path_exists(${1:col}, '${2:$.path}')",
    docsUrl: 'https://www.postgresql.org/docs/current/functions-json.html',
    requiresPath: true,
  },
]);

const PG_JSON: readonly Operator[] = Object.freeze([
  { token: '=', label: 'equals', snippetTemplate: "= '${1:value}'::json" },
  { token: '<>', label: 'not equals', snippetTemplate: "<> '${1:value}'::json" },
  { token: 'IS NULL', label: 'is null', snippetTemplate: 'IS NULL' },
  { token: 'IS NOT NULL', label: 'is not null', snippetTemplate: 'IS NOT NULL' },
  {
    token: '->',
    label: 'get field as json',
    snippetTemplate: "-> '${1:key}'",
    docsUrl: 'https://www.postgresql.org/docs/current/functions-json.html',
    requiresPath: true,
  },
  {
    token: '->>',
    label: 'get field as text',
    snippetTemplate: "->> '${1:key}'",
    docsUrl: 'https://www.postgresql.org/docs/current/functions-json.html',
    requiresPath: true,
  },
]);

export function postgresJsonOperatorsFor(
  classification: JsonClassification,
): readonly Operator[] {
  // `native-json` (non-binary) gets the narrower operator surface; every other
  // JSON kind (native-jsonb / check-isjson / loose-json-*) gets the full jsonb
  // lattice — loose-declared columns are expected to round-trip via `::jsonb`
  // casts on emit (Slice D3).
  return classification.kind === 'native-json' ? PG_JSON : PG_JSONB;
}
